import type { AskResponse } from "@shared/types";

// Read-only offline support: the last N conversation threads per project
// are cached in localStorage so an inspector who already asked something
// can still see it with no signal. This does NOT queue new questions
// offline — see the README for why that's deliberately out of scope for v1.
const MAX_CACHED_THREADS_PER_PROJECT = 20;

export interface CachedTurn {
  question: string;
  response: AskResponse;
}

/** One conversation — every follow-up updates this same entry (keyed by
 * threadId, the first turn's auditId) instead of appending a new one, so
 * the Recent Chats sidebar shows one row per conversation, not one per
 * answer. See AskPage.tsx. */
export interface CachedThread {
  threadId: string;
  turns: CachedTurn[]; // oldest first
  cachedAt: string;
}

export const ANSWER_CACHED_EVENT = "fieldbook:answer-cached";

// Bumped from v2 (projectId only, no userId) — reproduced live, a second
// person logging into the app on the same browser as an earlier user saw
// that user's Recent Chats, since localStorage is shared per-origin, not
// per-session. v2 entries are simply orphaned under their old key rather
// than needing defensive migration code for what's already a nice-to-have
// cache — see the v1->v2 precedent this same comment used to describe.
function key(userId: string, projectId: string): string {
  return `fieldbook:answers:v3:${userId}:${projectId}`;
}

export function cacheThread(userId: string, projectId: string, threadId: string, turns: CachedTurn[]): void {
  try {
    const existing = readCache(userId, projectId).filter((t) => t.threadId !== threadId);
    const next = [{ threadId, turns, cachedAt: new Date().toISOString() }, ...existing].slice(
      0,
      MAX_CACHED_THREADS_PER_PROJECT,
    );
    localStorage.setItem(key(userId, projectId), JSON.stringify(next));
    // The sidebar's recent-chats list reads this same cache but doesn't
    // otherwise know it just changed — a same-tab localStorage write never
    // fires the native `storage` event (that only fires in *other* tabs).
    window.dispatchEvent(new CustomEvent(ANSWER_CACHED_EVENT, { detail: { projectId } }));
  } catch {
    // Storage full or unavailable (private browsing) — offline cache is a
    // nice-to-have, never block the actual answer on it.
  }
}

export function readCache(userId: string, projectId: string): CachedThread[] {
  try {
    const raw = localStorage.getItem(key(userId, projectId));
    return raw ? (JSON.parse(raw) as CachedThread[]) : [];
  } catch {
    return [];
  }
}
